import React, { useState } from "react";
import { useExceptionsController } from "./useExceptionsController";
import { useClientController } from "../client/useClientController";
import { 
  ExceptionDetails,
  ExceptionHeader,
  ExceptionOverviewCards,
  ExceptionTableContainer,
  NoReportingPeriodState 
} from "./components";
import { ReportingPeriodTimeline } from "../reporting-periods";
import moment from "moment-timezone";

export const ExceptionsView: React.FC = () => {
  const {
    ruleExceptions,
    selectedRuleException,
    filters,
    loading,
    selectedReportingPeriod,
    setSelectedRuleExceptionId,
    updateRuleExceptionStatus,
    assignRuleException,
    updateRuleExceptionNotes,
    handleFilterChange,
  } = useExceptionsController();

  // Fetch client info to get list of members
  const { client: clientInfo } = useClientController();

  const [showFilters, setShowFilters] = useState(false);

  const handleBackFromDetails = () => {
    setSelectedRuleExceptionId(null);
  };

  // Get status counts for the overview cards
  const statusCounts = ruleExceptions.reduce((acc, exception) => {
    acc[exception.status] = (acc[exception.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Get risk level counts for the overview cards
  const riskLevelCounts = ruleExceptions.reduce((acc, exception) => {
    acc[exception.riskLevel] = (acc[exception.riskLevel] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Prefer members from client info; if unavailable, fall back to any assignedTo values we have
  const teamMembers = clientInfo?.members?.length
    ? clientInfo.members.map((m) => m.firstName + " " + m.lastName)
    : Array.from(
        new Set(
          ruleExceptions
            .map((e) => e.assignedTo)
            .filter((email): email is string => Boolean(email && email.trim()))
        )
      );

  // Check if any filters are active
  const hasActiveFilters = !!(filters.status || filters.assignedTo || filters.search);

  if (!selectedReportingPeriod) {
    return <NoReportingPeriodState />;
  }

  const formattedDate = moment(new Date(selectedReportingPeriod.period)).format("MMM Do, yyyy");

  return (
    <div className="min-h-screen">
      <ReportingPeriodTimeline />

      <div className="container mx-auto px-6 py-8 max-w-8xl">
        {selectedRuleException ? (
          <div className="bg-white rounded-xl shadow-sm border">
            <ExceptionDetails
              ruleException={selectedRuleException}
              onBack={handleBackFromDetails}
              onUpdateStatus={updateRuleExceptionStatus}
              onAssign={assignRuleException}
              teamMembers={teamMembers}
              onUpdateNotes={updateRuleExceptionNotes}
            />
          </div>
        ) : (
          <>
            <ExceptionHeader
              formattedDate={formattedDate}
              loading={loading}
              ruleExceptionsCount={ruleExceptions.length}
              hasActiveFilters={hasActiveFilters}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            <ExceptionOverviewCards
              statusCounts={statusCounts}
              riskLevelCounts={riskLevelCounts}
              totalExceptions={ruleExceptions.length}
            />

            <ExceptionTableContainer
              ruleExceptions={ruleExceptions}
              loading={loading}
              onSelectRuleException={setSelectedRuleExceptionId}
              onUpdateStatus={updateRuleExceptionStatus}
            />
          </>
        )}
      </div>
    </div>
  );
};
